// Spellbook list: grouped by category (Direct, Trap, Area), alphabetical
// within each, searchable, with the spells the lobby banned.
import { SPELL_CATEGORIES, SPELLS, SPELLS_BY_ID, spellCostLabel, type SpellCategory, type SpellDef, type SpellRule } from "@wp/shared";

export interface SpellEntry {
  def: SpellDef;
  /** Effective rule in this lobby: "banned" = the host banned it. */
  rule: SpellRule;
  cost: string;
}

const collator = new Intl.Collator("en", { sensitivity: "base" });

/** All spells, sorted by name, with overrides applied (unknown ids are ignored). */
export function spellEntries(
  overrides: Readonly<Record<string, SpellRule>> = {},
  spells: readonly SpellDef[] = SPELLS,
): SpellEntry[] {
  return spells
    .map((def) => {
      const banned = Object.hasOwn(overrides, def.id) && overrides[def.id] === "banned";
      const rule: SpellRule = banned ? "banned" : "legal";
      return { def, rule, cost: spellCostLabel(def) };
    })
    .sort((a, b) => collator.compare(a.def.name, b.def.name));
}

/** Category names as the spellbook prints them. */
export const CATEGORY_LABEL: Record<SpellCategory, string> = { direct: "Direct", trap: "Trap", area: "Area" };

/** What each category means, in one line (the spellbook's group headers and tips). */
export const CATEGORY_HINT: Record<SpellCategory, string> = {
  direct: "Aimed at a player. Counterspell reflects these.",
  trap: "Secret card spells: only you see them assembled.",
  area: "Hit the whole table.",
};

/** Entries split into the category groups, in category order (empty groups left out); entry order is kept. */
export function groupByCategory(entries: readonly SpellEntry[]): { category: SpellCategory; entries: SpellEntry[] }[] {
  return SPELL_CATEGORIES.map((category) => ({ category, entries: entries.filter((e) => e.def.category === category) })).filter(
    (g) => g.entries.length > 0,
  );
}

/**
 * Case-insensitive search. Every word of the query must appear in the name,
 * the description, the category ("direct", "trap", "area") or "banned".
 * Order stays alphabetical.
 */
export function searchSpells(entries: readonly SpellEntry[], query: string): SpellEntry[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...entries];
  const matches = entries.filter((e) => {
    const name = e.def.name.toLowerCase();
    const desc = e.def.description.toLowerCase();
    return words.every((w) => name.includes(w) || desc.includes(w) || e.def.category === w || e.rule === w);
  });
  return matches;
}

/** Only keep bans of known spells (lobby settings come from the network; every spell is legal unless banned). */
export function knownOverrides(overrides: Readonly<Record<string, SpellRule>>): Record<string, SpellRule> {
  const out: Record<string, SpellRule> = {};
  for (const [id, rule] of Object.entries(overrides)) if (SPELLS_BY_ID.has(id) && rule === "banned") out[id] = rule;
  return out;
}

/** Two-letter placeholder for the spell icon. */
export function spellInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const second = parts.length > 1 ? (parts[1]?.[0] ?? "") : (parts[0]?.[1] ?? "");
  return (first + second).toUpperCase();
}

/**
 * Assembly-style mnemonic for a spell, like an instruction in an old CPU
 * manual: "Magic Missile" -> "MAGMS", "Fireball" -> "FIRBL" (at most 5 letters).
 */
export function spellMnemonic(name: string): string {
  const words = name.toUpperCase().replace(/[^A-Z ]/g, "").split(/\s+/).filter(Boolean);
  if (words.length === 0) return "NOP";
  if (words.length === 1) {
    const w = words[0]!;
    // First three letters, then the remaining consonants.
    const rest = w.slice(3).replace(/[AEIOU]/g, "");
    return (w.slice(0, 3) + rest).slice(0, 5);
  }
  const head = words[0]!.slice(0, 3);
  const tail = words
    .slice(1)
    .map((w) => w[0] + w.slice(1).replace(/[AEIOU]/g, "").slice(0, 1))
    .join("");
  return (head + tail).slice(0, 5);
}

/** Opcode shown in the spellbook: its place in the A-Z list, as hex from $20. */
export function spellOpcode(index: number): string {
  return `$${(0x20 + index).toString(16).toUpperCase().padStart(2, "0")}`;
}
